import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { blue, orange, yellow, red, green } from "@mui/material/colors";
import DescriptionIcon from '@mui/icons-material/Description';
import HourglassFullIcon from '@mui/icons-material/HourglassFull';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GetAppIcon from '@mui/icons-material/GetApp';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import WarningIcon from '@mui/icons-material/Warning';
import RoomIcon from '@mui/icons-material/Room';
import { Chip, Typography } from "@mui/material";
import { ModalV2 } from "components/Modal/ModalV2";
import RegularButton from "components/CustomButtons/Button";
import { EndowmentModalContent } from "views/Closeouts-Settlements/EndowmentModalContent";
import { useToggle } from "hooks/useToggle";
import { useSearch } from "hooks/useSearch";
import { useRuleValidator } from "hooks/useRuleValidator";
import { PERMISSIONS } from "router/permissions";
import {
  checkInventoryIsOpen,
  ROUTE_INVENTORY_CLOSED_MESSAGE,
} from "hooks/fetch-hooks/useCheckInventoryStatus";
import { styles } from "./styles";
import { formatCustomDate } from "helpers/dateTools";
import { routesStatus } from "helpers/timeStatus";
import {
  getEndowment,
  fetchCausesAction,
  getSettlement,
  setUnitPin,
  generateTransferInvoice,
  downloadTransferInvoicePdf,
  transferInvoiceLoadingKey,
} from "redux/actions/routesStatus/routesStatusAction";

const common = {
  headerStyle: styles().thTable,
  filterable: true,
  resizable: false
};
const red1 = red[500];
const blue1 = blue[900];
const blue2 = blue[700];
const orange1 = orange[900];
const orange2 = orange[700];
const yellow1 = yellow[800];

const PAYMENTS_CONFIG = {
  CASH: { label: "EFECTIVO", color: green[800], bgColor: green[50] },
  CREDIT: { label: "CRÉDITO", color: blue[800], bgColor: blue[50] },
  CASH_DISCOUNT: { label: "EFE DESC", color: yellow1, bgColor: yellow[50] },
};

const PaymentChip = ({ payments }) => {
  if (!payments || payments === "MIXED") return null;
  const config = PAYMENTS_CONFIG[payments];
  if (!config) return null;
  return (
    <Chip
      label={config.label}
      size="small"
      style={{
        color: config.color,
        backgroundColor: config.bgColor,
        fontWeight: 600,
        fontSize: 10,
        marginLeft: 4,
      }}
    />
  );
};

/**
 * Botón de Factura de Traslado (Carta Porte).
 * Comportamiento:
 * - Sin el permiso TRANSFER-RENEW no se muestra nada (el backend lo exige para
 *   generar y para descargar).
 * - Si transferInvoicePdfToken tiene valor → Mostrar botón DESCARGAR (PDF verde)
 *   en cualquier ruta.
 * - Si la ruta ya tiene Carta Porte (transferInvoiceId) pero sin PDF → botón
 *   deshabilitado para no ofrecer "Generar" de nuevo.
 * - Si no hay Carta Porte → Mostrar botón GENERAR (PDF rojo) solo si la ruta
 *   está en status OPEN.
 *
 * El estado de carga se maneja por ruta (loadingTransferInvoice_<route>) para
 * que el spinner solo afecte a la fila que se está procesando.
 */
const TransferInvoiceButton = ({ original }) => {
  const dispatch = useDispatch();
  const hasTransferRule = useRuleValidator(PERMISSIONS.TRANSFER_RENEW);
  const { status, transferInvoicePdfToken, transferInvoiceId, transferInvoiceGenerated, route, unit, startDate } = original;

  const loadingKey = transferInvoiceLoadingKey(route);
  const isLoading = useSelector(
    ({ search }) => !!search?.isLoadingComp?.[loadingKey]
  );

  const handleGenerateTransferInvoice = useCallback(() => {
    dispatch(generateTransferInvoice(route));
  }, [dispatch, route]);

  const handleDownloadPdf = useCallback(() => {
    dispatch(downloadTransferInvoicePdf(route, transferInvoicePdfToken, { unit, startDate }));
  }, [dispatch, route, transferInvoicePdfToken, unit, startDate]);

  const isRouteOpen = status === routesStatus.OPEN.value;
  const hasToken = transferInvoicePdfToken != null && transferInvoicePdfToken !== "";

  // Generar y descargar requieren el permiso TRANSFER-RENEW
  if (!hasTransferRule) return null;

  // Si ya tiene token → la descarga está disponible sin importar el status
  if (hasToken) {
    return (
      <RegularButton
        size="sm"
        round
        justIcon
        color="transparent"
        tooltip="Descargar Carta Porte"
        onClick={handleDownloadPdf}
        loading={isLoading}
      >
        <GetAppIcon style={{ color: green[700] }} />
      </RegularButton>
    );
  }

  // Generar solo con ruta abierta
  if (!isRouteOpen) return null;

  // Carta Porte ya existe (o se generó en esta sesión) pero sin PDF → bloquear para no facturar dos veces
  if (transferInvoiceId || transferInvoiceGenerated) {
    return (
      <RegularButton
        size="sm"
        round
        justIcon
        color="transparent"
        tooltip="Carta Porte generada, vuelva a consultar las rutas para descargarla"
        disabled
      >
        <PictureAsPdfIcon style={{ color: green[700] }} />
      </RegularButton>
    );
  }

  // Si no tiene token → mostrar botón de generar
  return (
    <RegularButton
      size="sm"
      round
      justIcon
      color="transparent"
      tooltip="Generar Carta Porte"
      onClick={handleGenerateTransferInvoice}
      loading={isLoading}
    >
      <RestorePageIcon style={{ color: red1 }} />
    </RegularButton>
  );
};

export const columns = [
  {
    ...common,
    Header: "Unidad",
    accessor: "unit",
    filterMethod: (filter, row) => row.unit?.toLowerCase().includes(filter.value?.toLowerCase()),
    Cell: ({ original }) => {
      const { isPinned } = useSelector(({ routesStatus }) => routesStatus)
      const dispatch = useDispatch();
      const { restoreTable } = useSearch()
      const handlePint = useCallback(() => {
        restoreTable('startEndRoutes')
        dispatch(setUnitPin(original))
      }, [dispatch, original, restoreTable])
      return (
        <RegularButton
          color="transparent"
          onClick={handlePint}
        >{original.unit}
          {
            isPinned &&
            <RoomIcon style={{ color: blue[500] }} />
          }
        </RegularButton>
      )
    }
  },
  {
    ...common,
    Header: "Tipo",
    accessor: "type",
    filterMethod: (filter, row) =>
      row.type?.toLowerCase().includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Inicio",
    accessor: "startDate",
    resizable: true,
    minWidth: 150,
    filterMethod: (filter, row) =>
      row.startDate?.toLowerCase().includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Fin",
    accessor: "endDate",
    resizable: true,
    minWidth: 150,
    filterMethod: (filter, row) =>
      row.endDate?.toLowerCase().includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Estatus",
    accessor: "statusEsp",
    filterMethod: (filter, row) => String(row.statusEsp)?.toLowerCase().includes(filter.value?.toLowerCase()),
    getProps: (_) => ({
      style: {
        margin: 0,
        paddingTop: 0,
        paddingBottom: 0
      }
    }),
    Cell: ({ original }) => {
      const { statusEsp, color, bgColor } = original
      return (
        <div style={{
          backgroundColor: bgColor,
          alignContent: 'center',
          textAlign: 'center',
          fontWeight: 'bold',
          color: color,
          width: '100%',
          height: '100%'
        }}>
          {statusEsp}
        </div>
      )
    },
  },
  {
    ...common,
    Header: "Nº Servicio",
    accessor: "services",
    filterMethod: (filter, row) =>
      String(row.services)
        ?.toLowerCase()
        .includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Operador",
    accessor: "operator",
    resizable: true,
    minWidth: 200,
    filterMethod: (filter, row) =>
      String(row.operator)
        ?.toLowerCase()
        .includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Ayudante",
    accessor: "helper",
    filterMethod: (filter, row) =>
      String(row.helper)
        ?.toLowerCase()
        .includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Accion",
    Cell: ({ original }) => {
      const { station, endowment } = useSelector(state => state.routesStatus)
      const { label } = station || {}
      const { isLoadingComp } = useSelector(({ search }) => search)
      const [open, setOpen] = useToggle()
      const [openForce, setOpenForce] = useToggle()
      const [checkingInv, _c, setCheckingInv] = useToggle()
      const [invClosed, _ic, setInvClosed] = useToggle()
      const hist = useHistory()
      const dispatch = useDispatch()
      const { setSnackbar } = useSearch()
      const { route, idStart, idEnd, status, type, unitManualConf, unitCapacity, isMaintenance, startDate } = original

      // Al abrir/cerrar el modal se limpia el resultado de la validación previa
      const handleToggleForce = () => {
        setInvClosed(false)
        setOpenForce()
      }

      const handleSubmit = async () => {
        if (checkingInv) return
        if (!station?.id) {
          setSnackbar("Seleccione una Planta", "danger", "tc")
          return
        }
        setCheckingInv(true)
        try {
          const isOpen = await checkInventoryIsOpen(station.id, formatCustomDate(startDate, '-'))
          if (!isOpen) {
            // Sin el modal abierto el mensaje en línea no se ve: se avisa por snackbar
            if (openForce) setInvClosed(true)
            else setSnackbar(ROUTE_INVENTORY_CLOSED_MESSAGE, "danger", "tc")
            return
          }
        } catch (err) {
          setSnackbar(`${err || 'Error al consultar el inventario'}`, "danger", "tc")
          return
        } finally {
          setCheckingInv(false)
        }

        dispatch(fetchCausesAction())
        dispatch(getSettlement(
          {route, unitCapacity},
          idStart,
          idEnd,
          hist,
          false,
          ''
        ))
      }

      const handleWarning = useCallback(() => {
        dispatch(setSnackbar(`UNIDAD ${original?.unit} EN MANTENIMIENTO, CONTACTA A SOPORTE EN CASO DE SER NECESARIO`))
      }, [dispatch, original?.unit, setSnackbar])

      const handleEndowment = useCallback(() => dispatch(getEndowment(route, setOpen)), [dispatch, route, setOpen])

      return (
        <>
          <RegularButton
            size="sm"
            round
            justIcon
            color="transparent"
            onClick={unitManualConf ? handleToggleForce : handleSubmit}
            tooltip={unitManualConf ? 'Liquidación Manual' : 'Ver liquidación'}
            disabled={isMaintenance ? true : false  || idEnd !== null || unitManualConf ? false : true}
            style={{ transition: '.3s ease' }}
          >
            {
              !(isLoadingComp?.loadingSettlement || checkingInv)
                ? <DescriptionIcon style={{ margin: 0, color: `${unitManualConf ? orange1 : blue1}` }} />
                : <HourglassFullIcon style={{ margin: 0, color: `${unitManualConf ? orange2 : blue2}` }} />
            }
          </RegularButton>

          {
            status === routesStatus.OPEN.value &&
            <RegularButton
              size="sm"
              round
              justIcon
              color="transparent"
              onClick={handleEndowment}
              style={{ color: blue1 }}
              tooltip="Dotación de unidad"
            >
              +
            </RegularButton>
          }
          <TransferInvoiceButton original={original} />

          {
            isMaintenance &&
            <RegularButton
              size="sm"
              round
              justIcon
              color="transparent"
              tooltip="En Mantenimiento"
              onClick={handleWarning}
            >
              <WarningIcon style={{ color: yellow1 }} />
            </RegularButton>
          }

          <PaymentChip payments={original?.payments} />

          <ModalV2
            open={open}
            onClose={setOpen}
            title="Dotación de Unidad"
            toRight
            loading={isLoadingComp?.loadingSettlement}
          >
            <EndowmentModalContent data={{
              ...original,
              ...endowment, station: label, type: type
            }} onClose={setOpen} fromRoutes />
          </ModalV2>

          <ModalV2
            open={openForce}
            onClose={handleToggleForce}
            justConfirm
            title={`¿Seguro que desea liquidar de manera manual?`}
          >
            <div style={{ width: '100%' }}>
              {
                invClosed &&
                <Typography
                  variant="body1"
                  style={{ color: red1, textAlign: "center", marginBottom: 16 }}
                >
                  {ROUTE_INVENTORY_CLOSED_MESSAGE}
                </Typography>
              }
              <div style={{
                display: "flex",
                justifyContent: invClosed ? "center" : "space-between",
                alignItems: "center",
                gap: 8
              }}>
                {
                  invClosed
                    ? <RegularButton
                      color="info"
                      onClick={handleToggleForce}
                    >
                      Cerrar
                    </RegularButton>
                    : <>
                      <RegularButton
                        color="info"
                        onClick={handleToggleForce}
                        disabled={isLoadingComp?.loadingSettlement || checkingInv}
                      >
                        Cancelar
                      </RegularButton>
                      <RegularButton
                        color="danger"
                        onClick={handleSubmit}
                        loading={isLoadingComp?.loadingSettlement || checkingInv}
                      >
                        Confirmar
                      </RegularButton>
                    </>
                }
              </div>
            </div>
          </ModalV2>
        </>
      )

    }
  }
];

export const columns2 = [
  {
    ...common,
    Header: "Unidad",
    accessor: "unit",
    filterMethod: (filter, row) => row.unit?.toLowerCase().includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Tipo",
    accessor: "type",
    filterMethod: (filter, row) =>
      row.type?.toLowerCase().includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Dispensador",
    accessor: "name",
    filterMethod: (filter, row) =>
      row.name?.toLowerCase().includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Inicio",
    accessor: "startDate",
    resizable: true,
    minWidth: 150,
    filterMethod: (filter, row) =>
      row.startDate?.toLowerCase().includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Fin",
    accessor: "endDate",
    resizable: true,
    minWidth: 150,
    filterMethod: (filter, row) =>
      row.endDate?.toLowerCase().includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Estatus",
    accessor: "statusEsp",
    filterMethod: (filter, row) => String(row.statusEsp)?.toLowerCase().includes(filter.value?.toLowerCase()),
    getProps: (_) => ({
      style: {
        margin: 0,
        paddingTop: 0,
        paddingBottom: 0
      }
    }),
    Cell: ({ original }) => {
      const { statusEsp, color, bgColor } = original
      return (
        <div style={{
          backgroundColor: bgColor,
          alignContent: 'center',
          textAlign: 'center',
          fontWeight: 'bold',
          color: color,
          width: '100%',
          height: '100%'
        }}>
          {statusEsp}
        </div>
      )
    },
  },
  {
    ...common,
    Header: "Nº Servicio",
    accessor: "services",
    filterMethod: (filter, row) =>
      String(row.services)
        ?.toLowerCase()
        .includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Operador",
    accessor: "operator",
    resizable: true,
    minWidth: 200,
    filterMethod: (filter, row) =>
      String(row.operator)
        ?.toLowerCase()
        .includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Ayudantes",
    accessor: "helper",
    filterMethod: (filter, row) =>
      String(row.helper)
        ?.toLowerCase()
        .includes(filter.value?.toLowerCase()),
  },
  {
    ...common,
    Header: "Accion",
    Cell: ({ original }) => {
      const { station, endowmentLoading, endowment } = useSelector(state => state.routesStatus)
      const { label } = station || {}
      const { isLoadingComp } = useSelector(({ search }) => search)
      const [open, setOpen] = useToggle()
      const [openForce, setOpenForce] = useToggle()
      const [checkingInv, _c, setCheckingInv] = useToggle()
      const [invClosed, _ic, setInvClosed] = useToggle()
      const hist = useHistory()
      const dispatch = useDispatch()
      const { setSnackbar } = useSearch()
      const { route, idStart, idEnd, status, type, unitManualConf, unitCapacity, isMaintenance, startDate } = original

      // Al abrir/cerrar el modal se limpia el resultado de la validación previa
      const handleToggleForce = () => {
        setInvClosed(false)
        setOpenForce()
      }

      const handleSubmit = async () => {
        if (checkingInv) return
        if (!station?.id) {
          setSnackbar("Seleccione una Planta", "danger", "tc")
          return
        }
        setCheckingInv(true)
        try {
          const isOpen = await checkInventoryIsOpen(station.id, formatCustomDate(startDate, '-'))
          if (!isOpen) {
            // Sin el modal abierto el mensaje en línea no se ve: se avisa por snackbar
            if (openForce) setInvClosed(true)
            else setSnackbar(ROUTE_INVENTORY_CLOSED_MESSAGE, "danger", "tc")
            return
          }
        } catch (err) {
          setSnackbar(`${err || 'Error al consultar el inventario'}`, "danger", "tc")
          return
        } finally {
          setCheckingInv(false)
        }

        dispatch(fetchCausesAction())
        dispatch(getSettlement(
          {route, unitCapacity},
          idStart,
          idEnd,
          hist,
          true,
          original?.idDispenser
        ))
      }

      const handleEndowment = useCallback(() => dispatch(getEndowment(route, setOpen)), [dispatch, route, setOpen])

      const handleWarning = useCallback(() => {
        setSnackbar(`UNIDAD ${original?.unit} EN MANTENIMIENTO, CONTACTA A SOPORTE EN CASO DE SER NECESARIO`)
      }, [original?.unit, setSnackbar])

      return (
        <>
          <RegularButton
            size="sm"
            round
            justIcon
            color="transparent"
            onClick={unitManualConf ? handleToggleForce : handleSubmit}
            tooltip={unitManualConf ? 'Liquidación Manual' : 'Ver liquidación'}
            disabled={isMaintenance ? true : false || idEnd !== null || unitManualConf ? false : true}
            style={{ transition: '.3s ease' }}
            loading={isLoadingComp?.loadingSettlement || checkingInv}
          >
            {
              !(isLoadingComp?.loadingSettlement || checkingInv)
                ? <DescriptionIcon style={{ margin: 0, color: `${unitManualConf ? orange1 : blue1}` }} />
                : <HourglassFullIcon style={{ margin: 0, color: `${unitManualConf ? orange2 : blue2}` }} />
            }
          </RegularButton>
          {
            status === routesStatus.OPEN.value &&
            <RegularButton
              size="sm"
              round
              justIcon
              color="transparent"
              onClick={handleEndowment}
              style={{ color: blue1 }}
              tooltip="Dotación de unidad"
            >
              +
            </RegularButton>
          }
          {
            isMaintenance &&
            <RegularButton
              size="sm"
              round
              justIcon
              color="transparent"
              tooltip="En Mantenimiento"
              onClick={handleWarning}
            >
              <WarningIcon style={{ color: yellow1 }} />
            </RegularButton>
          }

          <PaymentChip payments={original?.payments} />

          <ModalV2
            open={open}
            onClose={setOpen}
            title="Dotación de unidad"
            toRight
            isLoading={endowmentLoading}
          >
            <EndowmentModalContent data={{
              ...original,
              ...endowment, station: label, type: type
            }} onClose={setOpen} fromRoutes />
          </ModalV2>

          <ModalV2
            open={openForce}
            onClose={handleToggleForce}
            justConfirm
            title={`¿Seguro que desea liquidar de manera manual?`}
          >
            <div style={{ width: '100%' }}>
              {
                invClosed &&
                <Typography
                  variant="body1"
                  style={{ color: red1, textAlign: "center", marginBottom: 16 }}
                >
                  {ROUTE_INVENTORY_CLOSED_MESSAGE}
                </Typography>
              }
              <div style={{
                display: "flex",
                justifyContent: invClosed ? "center" : "space-between",
                alignItems: "center",
                gap: 8
              }}>
                {
                  invClosed
                    ? <RegularButton
                      color="info"
                      onClick={handleToggleForce}
                    >
                      Cerrar
                    </RegularButton>
                    : <>
                      <RegularButton
                        color="info"
                        onClick={handleToggleForce}
                        disabled={isLoadingComp?.loadingSettlement || checkingInv}
                      >
                        Cancelar
                      </RegularButton>
                      <RegularButton
                        color="danger"
                        onClick={handleSubmit}
                        loading={isLoadingComp?.loadingSettlement || checkingInv}
                      >
                        Confirmar
                      </RegularButton>
                    </>
                }
              </div>
            </div>
          </ModalV2>
        </>
      )

    }
  }
];
